import React, { useEffect, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Inspection } from 'api/Responses/OrderResponses';
import Theme from 'App.theme';
import Checkbox from 'components/Checkbox/Checkbox';
import { useLanguage } from 'context/AppContext';
import { formatDate } from 'utils/helpers/DateTimeFormat.helpers';
import * as Styled from '../DeliveryPortalPage.styles';

interface DuplicatesTableProps {
  duplicates: Inspection[];
  setDuplicates: (data: Inspection[]) => void;
}

const DuplicatesTable: React.FC<DuplicatesTableProps> = ({ duplicates, setDuplicates }) => {
  const [language] = useLanguage();
  const [selectAllCheckbox, setSelectAllCheckbox] = useState(true);

  useEffect(() => {
    const onLoadDuplicates = duplicates.map(element => {
      element.set_found && (element.ignore = false);
      return element;
    });
    setDuplicates(onLoadDuplicates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean, index: number): void => {
    const resultArray = [...duplicates];
    resultArray[index].ignore = !checked;
    setDuplicates(resultArray);

    const allSelected = resultArray.every(element => element.ignore === false);
    setSelectAllCheckbox(allSelected);
  };

  const handleSelectAllCheckbox = (checked: boolean): void => {
    const resultArray = duplicates.map(element => {
      element.ignore = !checked;
      return element;
    });

    setDuplicates(resultArray);
    setSelectAllCheckbox(checked);
  };

  return (
    <Styled.DropTableContainer style={{ maxHeight: '30rem' }}>
      <Table sx={{ maxHeight: 'auto' }} stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{language.DeliveryPortal.Table.deliveryNumber}</TableCell>
            <TableCell>{language.DeliveryPortal.Table.kitCode}</TableCell>
            <TableCell align="center">{language.DeliveryPortal.Table.kitSerial}</TableCell>
            <TableCell align="center">{language.DeliveryPortal.Table.shippingDate}</TableCell>
            <TableCell align="center" size="small">
              {language.DeliveryPortal.Table.previousOrderCompleted}
            </TableCell>
            <TableCell align="center" size="small">
              {language.DeliveryPortal.Table.setExists}
            </TableCell>
            <TableCell align="center" size="small">
              {language.DeliveryPortal.Table.validGsOneCode}
            </TableCell>
            <TableCell align="center" size="small">
              {language.DeliveryPortal.Table.orderExists}
            </TableCell>
            <TableCell align="center" size="small">
              {language.DeliveryPortal.Table.overwrite}
              <Checkbox
                checked={selectAllCheckbox}
                onChange={(e, checked) => {
                  handleSelectAllCheckbox(checked);
                }}
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {duplicates.length ? (
            duplicates.map((inspection, index) => {
              return (
                <TableRow
                  key={`${inspection.delivery_number}-${inspection.gs_one_barcode}`}
                  style={
                    !inspection.ignore && inspection.set_found
                      ? { background: Theme.greys.GREY0 }
                      : !inspection.set_found
                      ? { background: '#ffffff' }
                      : {}
                  }
                >
                  <TableCell>{inspection.delivery_number}</TableCell>
                  <TableCell>{inspection.display_code}</TableCell>
                  <TableCell align="center">{inspection.set_serial}</TableCell>
                  <TableCell align="center">{formatDate(inspection.shipping_date, false)}</TableCell>
                  <TableCell align="center" size="small">
                    {inspection.kit_order_status === 'CONFIRMED' ? (
                      <CheckCircleOutlineIcon color="success" />
                    ) : inspection.kit_order_status === 'UNCONFIRMED' ? (
                      <CancelIcon color="error" />
                    ) : (
                      <Typography>N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" size="small">
                    {inspection.set_found ? <CheckCircleOutlineIcon color="success" /> : <CancelIcon color="error" />}
                  </TableCell>
                  <TableCell align="center" size="small">
                    {inspection.valid_gs_one_barcode ? (
                      <CheckCircleOutlineIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </TableCell>
                  <TableCell align="center" size="small">
                    {inspection.order_already_exists ? (
                      <CancelIcon color="error" />
                    ) : (
                      <CheckCircleOutlineIcon color="success" />
                    )}
                  </TableCell>
                  <TableCell align="center" size="small">
                    {!inspection.set_found || !inspection.valid_gs_one_barcode ? (
                      <Typography>N/A</Typography>
                    ) : (
                      <Checkbox
                        checked={!inspection.ignore}
                        onChange={(e, checked) => {
                          handleCheckboxChange(e, checked, index);
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell align="center" colSpan={6}>
                <Typography>{language.DeliveryPortal.Table.noResults}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Styled.DropTableContainer>
  );
};

export default DuplicatesTable;
